import React from 'react';
import { BusinessAxisScore, BusinessBand } from '../../types/business';

const fa = (n: number) => n.toLocaleString('fa-IR');
const faDec = (n: number) => n.toLocaleString('fa-IR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * رنگ بازه‌ها. **دقت کنید جهت اینجا برعکس پرسشنامهٔ مسیر رشد است**:
 * آنجا امتیاز کم یعنی نیاز فوری، اینجا امتیاز کم یعنی وضعیت بد.
 * پس قرمز روی امتیاز پایین می‌نشیند و سبز روی امتیاز بالا.
 */
export const BAND_COLORS: Record<BusinessBand, string> = {
  CRITICAL: '#d03b3b',
  NEEDS_WORK: '#fab219',
  RELIABLE: '#3B82C4',
  STRENGTH: '#0ca30c',
};

export const BAND_LABELS: Record<BusinessBand, string> = {
  CRITICAL: 'بحرانی',
  NEEDS_WORK: 'نیازمند بهبود',
  RELIABLE: 'قابل اتکا',
  STRENGTH: 'نقطه قوت',
};

/** مرز بازه‌ها روی محور ۱ تا ۵ — همان جدول بخش D فرم */
const BAND_STOPS: Array<{ key: BusinessBand; from: number; to: number }> = [
  { key: 'CRITICAL', from: 1, to: 2.5 },
  { key: 'NEEDS_WORK', from: 2.5, to: 3.5 },
  { key: 'RELIABLE', from: 3.5, to: 4.2 },
  { key: 'STRENGTH', from: 4.2, to: 5 },
];

export const BandChip: React.FC<{ band: BusinessBand; className?: string }> = ({
  band,
  className = '',
}) => (
  <span
    className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10.5px] font-black ${className}`}
    style={{
      borderColor: `${BAND_COLORS[band]}44`,
      backgroundColor: `${BAND_COLORS[band]}12`,
      color: BAND_COLORS[band],
    }}
  >
    <span
      aria-hidden
      className="h-2 w-2 rounded-full"
      style={{ backgroundColor: BAND_COLORS[band] }}
    />
    {BAND_LABELS[band]}
  </span>
);

// ─── نمودار شش محور ──────────────────────────────────────────────────────────

interface AxisChartProps {
  scores: BusinessAxisScore[];
  /** فشرده برای کارت‌های کوچک */
  dense?: boolean;
}

/**
 * هر محور یک ردیف روی محور مشترک ۱ تا ۵، با سایهٔ چهار بازهٔ تفسیر در پس‌زمینه.
 * محور از راست (۱) به چپ (۵) می‌رود تا با جهت خواندن فارسی هم‌راستا باشد؛
 * پس نقطهٔ چپ‌تر یعنی وضعیت بهتر.
 */
export const BusinessAxisChart: React.FC<AxisChartProps> = ({ scores, dense }) => {
  if (!scores.length) return null;

  /** موقعیت درصدی از لبهٔ راست روی بازهٔ ۱ تا ۵ */
  const pct = (value: number) => ((value - 1) / 4) * 100;
  const labelWidth = dense ? 'w-24' : 'w-28 sm:w-36';

  return (
    <figure className="m-0">
      <div className="mb-1.5 flex items-center gap-3">
        <div className={`${labelWidth} shrink-0`} />
        <div className="relative h-4 flex-1">
          {[1, 2, 3, 4, 5].map((tick) => (
            <span
              key={tick}
              className="absolute text-[9.5px] font-black tabular-nums text-slate-400"
              style={{ right: `${pct(tick)}%`, transform: 'translateX(50%)' }}
            >
              {fa(tick)}
            </span>
          ))}
        </div>
        <div className="w-12 shrink-0" />
      </div>

      <div className="space-y-1">
        {scores.map((axis) => (
          <div key={axis.axisId} className="flex items-center gap-3 rounded-xl px-1 py-1.5">
            <div className={`${labelWidth} shrink-0`}>
              <p className="truncate text-[12px] font-black text-slate-800">{axis.title}</p>
              {!dense && axis.subtitle ? (
                <p className="truncate text-[9.5px] font-bold text-slate-400">{axis.subtitle}</p>
              ) : null}
            </div>

            <div className="relative h-7 flex-1">
              {/* سایهٔ بازه‌های تفسیر — خواننده بدون افسانه می‌فهمد کجای طیف است */}
              {BAND_STOPS.map((stop) => (
                <span
                  key={stop.key}
                  aria-hidden
                  className="absolute inset-y-0 my-auto h-4 first:rounded-r-md last:rounded-l-md"
                  style={{
                    right: `${pct(stop.from)}%`,
                    width: `${pct(stop.to) - pct(stop.from)}%`,
                    backgroundColor: BAND_COLORS[stop.key],
                    opacity: 0.1,
                  }}
                />
              ))}
              <span
                aria-hidden
                className="absolute inset-y-0 my-auto h-[3px] rounded-full transition-all duration-500"
                style={{
                  right: 0,
                  width: `${pct(axis.average)}%`,
                  backgroundColor: BAND_COLORS[axis.band],
                  opacity: 0.35,
                }}
              />
              <span
                className="absolute inset-y-0 my-auto h-3.5 w-3.5 rounded-full border-2 border-white shadow transition-all duration-500"
                style={{
                  right: `${pct(axis.average)}%`,
                  transform: 'translateX(50%)',
                  backgroundColor: BAND_COLORS[axis.band],
                }}
                role="img"
                aria-label={`${axis.title}: میانگین ${faDec(axis.average)} از ۵ — ${axis.bandLabel}`}
              />
            </div>

            <span
              className="w-12 shrink-0 text-left text-[12.5px] font-black tabular-nums"
              style={{ color: BAND_COLORS[axis.band] }}
            >
              {faDec(axis.average)}
            </span>
          </div>
        ))}
      </div>

      <figcaption className="mt-3 flex flex-wrap items-center justify-center gap-x-3.5 gap-y-1 border-t border-slate-100 pt-2.5 text-[10px] font-black text-slate-400">
        <span>میانگین هر محور از ۵ — بیشتر یعنی بهتر</span>
        {BAND_STOPS.map((stop) => (
          <span key={stop.key} className="inline-flex items-center gap-1">
            <span
              aria-hidden
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: BAND_COLORS[stop.key] }}
            />
            {BAND_LABELS[stop.key]}
          </span>
        ))}
      </figcaption>
    </figure>
  );
};

// ─── امتیاز کل ───────────────────────────────────────────────────────────────

export const BusinessOverallGauge: React.FC<{
  score: number;
  band: BusinessBand;
  bandLabel: string;
  direction?: string | null;
}> = ({ score, band, bandLabel, direction }) => {
  const pct = ((score - 1) / 4) * 100;

  return (
    <div className="text-center">
      <p className="text-[11px] font-black text-slate-400">امتیاز کل کسب‌وکار</p>
      <p className="mt-1 text-4xl font-black tabular-nums" style={{ color: BAND_COLORS[band] }}>
        {faDec(score)}
        <span className="text-base font-black text-slate-300"> / ۵</span>
      </p>
      <div className="mx-auto mt-3 max-w-xs">
        <div className="relative h-2 overflow-hidden rounded-full">
          {BAND_STOPS.map((stop) => (
            <span
              key={stop.key}
              aria-hidden
              className="absolute inset-y-0"
              style={{
                right: `${((stop.from - 1) / 4) * 100}%`,
                width: `${((stop.to - stop.from) / 4) * 100}%`,
                backgroundColor: BAND_COLORS[stop.key],
                opacity: 0.22,
              }}
            />
          ))}
          <span
            className="absolute inset-y-0 w-[3px] rounded-full"
            style={{ right: `${pct}%`, backgroundColor: BAND_COLORS[band] }}
          />
        </div>
      </div>
      <div className="mt-2.5 flex justify-center">
        <BandChip band={band} />
      </div>
      {direction ? (
        <p className="mx-auto mt-2 max-w-sm text-[11.5px] font-bold leading-7 text-slate-500">
          جهت پیشنهادی: {direction}
        </p>
      ) : null}
      <span className="sr-only">{bandLabel}</span>
    </div>
  );
};
